import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  OverlayTrigger,
  Popover,
  ProgressBar
} from 'react-bootstrap';
import { fetchHVAScorecardDashData1 } from 'services/hvaqa.service';
import QAScorecardData2 from './QADashboardScorecardData2';
import HVASpinner from 'components/base/HVASpinner';
import { numberFormat } from 'helpers/utils';
import ScoredVsNonScoredChart from 'components/charts/ScoredVsNonScoredChart';

interface QAScorecardData1Props {
  scorecardid: number;
  startdate: Date;
  enddate: Date;
  serviceid: number;
  campaignid: number;
  agentid: number;
}

const QAScorecardData1: React.FC<QAScorecardData1Props> = ({
  scorecardid,
  startdate,
  enddate,
  serviceid,
  campaignid,
  agentid
}) => {
  // eslint-disable-next-line
  const [statisticsdata, setStatisticsData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Función para obtener los datos
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchHVAScorecardDashData1({
        scorecardid,
        serviceid,
        campaignid,
        agentid,
        datefrom: startdate,
        dateto: enddate
      });
      setStatisticsData(response);
    } catch (err) {
      setError('Error al recuperar los datos del scorecard');
    } finally {
      setLoading(false);
    }
  }, [scorecardid, startdate, enddate, serviceid, campaignid, agentid]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Lógica para asignar el color de fondo del Card basado en el averagescore
  const getCardBackgroundClass = () => {
    const score = statisticsdata?.averagescore;
    if (score == null) {
      return 'bg-secondary-light';
    } else if (score < 6) {
      return 'bg-primary';
    } else if (score >= 6 && score < 8) {
      return 'bg-warning';
    } else if (score >= 8) {
      return 'bg-success';
    }
  };

  const getPercentage = () => {
    const totalCalls = statisticsdata?.totalcalls || 0;
    const scoredCalls = statisticsdata?.scoredcalls || 0;

    if (totalCalls > 0) {
      return (scoredCalls / totalCalls) * 100;
    }
    return 0;
  };

  return (
    <>
      {loading ? (
        <Row className="justify-content-center align-items-center pt-3 w-100">
          <HVASpinner />
        </Row>
      ) : (
        <>
          <Row className="mb-1 mx-0 p-0 pt-3 justify-content-start align-items-stretch d-flex">
            {/* Primera columna con ancho fijo de 185px */}
            <Col
              style={{ width: '185px', flex: '0 0 185px' }}
              className="p-0 me-2 d-flex"
            >
              <OverlayTrigger
                trigger="click"
                placement="top"
                overlay={
                  <Popover id="popover" className="shadow-lg">
                    <Popover.Body>
                      <div style={{ width: '200px' }}>
                        <p className="lh-sm fs-9">
                          Card background color based on score value:
                        </p>
                        <ProgressBar style={{ height: '15px', width: '100' }}>
                          <ProgressBar
                            style={{
                              backgroundColor: '#c00000'
                            }}
                            now={56}
                            label="0-6"
                            className="lh-1 text-secondary-subtle fw-bold"
                          />
                          <ProgressBar
                            variant="warning"
                            now={22}
                            label="6-8"
                            className="lh-1 text-secondary-subtle fw-bold"
                          />
                          <ProgressBar
                            variant="success"
                            now={22}
                            label="8-10"
                            className="lh-1 text-secondary-subtle fw-bold"
                          />
                        </ProgressBar>
                      </div>
                    </Popover.Body>
                  </Popover>
                }
              >
                <Card
                  className={`text-center pt-0 ${getCardBackgroundClass()}`}
                  style={{ width: '185px', height: '100%' }}
                >
                  <span className="fs-9 p-0 pt-3 text-secondary-subtle">
                    Average Score
                  </span>
                  <h1 className="text-secondary-subtle pb-2">
                    {statisticsdata && statisticsdata.averagescore !== null
                      ? statisticsdata.averagescore.toFixed(1)
                      : 'N/A'}
                  </h1>
                </Card>
              </OverlayTrigger>
            </Col>

            {/* Segunda columna que ocupa el espacio restante */}
            <Col className="px-0 d-flex align-items-stretch">
              <Card className="w-100 h-100">
                <Card.Body className="fs-9 pt-0 pb-0 h-100 d-flex flex-column">
                  <Row className="flex-grow-1 mb-0">
                    <Col className="d-flex flex-column justify-content-center">
                      <div>
                        Number of elegible calls for this scorecard:{' '}
                        <span className="fw-bold">
                          {statisticsdata?.totalcalls
                            ? numberFormat(statisticsdata.totalcalls)
                            : 'N/A'}
                        </span>
                      </div>
                      <div>
                        Number of scored calls for this scorecard:{' '}
                        <span className="fw-bold">
                          {statisticsdata?.scoredcalls
                            ? numberFormat(statisticsdata.scoredcalls)
                            : 'N/A'}
                        </span>
                      </div>
                    </Col>
                    <Col
                      xs={2}
                      xl={2}
                      className="p-0 h-100 d-flex align-items-center justify-content-end"
                    >
                      <ScoredVsNonScoredChart percentage={getPercentage()} />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          {statisticsdata && statisticsdata.scoredcalls > 0 && (
            <Row className="mb-1 mx-0 p-0 pt-3 justify-content-start align-items-center">
              <div className="px-0 mx-0">
                <QAScorecardData2
                  scorecardid={scorecardid}
                  startdate={startdate}
                  enddate={enddate}
                  serviceid={serviceid}
                  campaignid={campaignid}
                  agentid={agentid}
                />
              </div>
            </Row>
          )}
        </>
      )}
      {error && <Row className="mt-3 text-danger">{error}</Row>}
    </>
  );
};

export default React.memo(QAScorecardData1);
